import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import createHelpPlacingOrderForm from "@tCentro/Handlers/CENHelpPlacingOrderFormHandler";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { RefObject, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    submitButtonText = "submitButtonText",
    description = "description",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
    };
}
const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: React.RefObject<ReCAPTCHA | null> }, boolean>(
        validateReCaptcha,
    ),
    createHelpPlacingOrderForm,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CENHelpPlacingOrderFormStyles {
    container?: GridContainerProps;
    successModal?: ModalPresentationProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    continueButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    modalContainer?: GridContainerProps;
    mainContainer: InjectableCss;
    inputStyles?: TextFieldProps;
    submitButton?: InjectableCss;
    textAreaStyles?: TextAreaPresentationProps;
    captchaStyles?: InjectableCss;
}

export const cenHelpPlacingOrderFormStyles: CENHelpPlacingOrderFormStyles = {
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    inputStyles: {
        errorProps: {
            css: css`
                margin-left: 24px;
            `,
        },
        cssOverrides: {
            formField: css`
                padding-top: 24px;
                label {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;
                    min-height: auto !important;
                }
            `,
            formInputWrapper: css`
                margin-top: 8px !important;
                input {
                    background: ${COLORS.background.grayShade} !important;
                    border-radius: 40px !important;
                    padding: 12px 24px !important;
                    min-height: 48px !important;

                    &::placeholder {
                        font-size: 14px;
                        font-style: normal;
                        font-weight: 400;
                        line-height: 24px;
                    }
                }
            `,
        },
    },

    textAreaStyles: {
        errorProps: {
            css: css`
                margin-left: 24px;
            `,
        },
        css: css`
            background: ${COLORS.background.grayShade} !important;
            border-radius: 40px !important;
            padding: 20px !important;
        `,
        labelProps: {
            css: css`
                margin-top: 24px;
                margin-left: 24px;
                color: ${getProp("theme.typography.p.color")};
            `,
        },
    },
    mainContainer: {
        css: css`
            padding-left: 64px;
            border-left: 2px solid #f2f2f2;
            height: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                        border: none;
                    `,
                    css`
                        padding-left: 0px;
                        border: none;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    submitButton: {
        css: css`
            margin-top: 24px;
            border-radius: 24px;
        `,
    },
    captchaStyles: {
        css: css`
            margin-top: 24px;
        `,
    },
};

const styles = cenHelpPlacingOrderFormStyles;

const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.(?!usa$)\\w+([-.]\\w+)*", "i");

const CENHelpPlacingOrderForm = ({ id, fields, createHelpPlacingOrderForm, validateReCaptcha }: Props) => {
    const [fullname, setFullname] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [message, setMessage] = useState("");

    const fullnameRequiredFieldMessage = siteMessage("fullnameRequiredFieldMessage");
    const emailAddressRequiredFieldMessage = siteMessage("emailAddressRequiredFieldMessage");
    const invalidEmailAddressMessage = siteMessage("invalidEmailAddressMessage");
    const messageRequiredFieldMessage = siteMessage("messageRequiredFieldMessage");

    const [emailAddressError, setEmailAddressError] = useState(emailAddressRequiredFieldMessage);
    const [fullnameError, setFullnameError] = useState(fullnameRequiredFieldMessage);
    const [messageError, setMessageError] = useState(messageRequiredFieldMessage);
    const [invalidEmailAddressError, setInvalidEmailAddressError] = useState(invalidEmailAddressMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    const fullnameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFullname(event.target.value);
        validateFullNameHandler(event.target.value);
    };

    const validateFullNameHandler = (fullname: string) => {
        const errorMessage = !fullname ? fullnameRequiredFieldMessage : "";
        setFullnameError(errorMessage);
        return !errorMessage;
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.target.value);
        validateEmailAddressHandler(event.target.value);
    };

    const validateEmailAddressHandler = (emailAddress: string) => {
        let errorMessage = !emailAddress ? emailAddressRequiredFieldMessage : "";
        if (emailAddress && !emailRegexp.test(emailAddress)) {
            errorMessage = "Invalid email address";
        }

        setEmailAddressError(errorMessage);
        return !errorMessage;
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
        validateMessageHandler(event.target.value);
    };

    const validateMessageHandler = (message: string) => {
        const errorMessage = !message ? messageRequiredFieldMessage : "";
        setMessageError(errorMessage);
        return !errorMessage;
    };

    const companyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyName(event.target.value);
    };

    const validateSubmitEnabled = () => {
        return !(fullnameError || messageError || emailAddressError);
    };

    const isSubmitEnabled = validateSubmitEnabled();

    const submitFormHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        function getDataLayer() {
            return (window as any).dataLayer;
        }

        const dataLayer = getDataLayer();
        if (dataLayer) {
            dataLayer.push({
                event: `CenHelpPlacingOrderForm`,
            });
        }

        setIsButtonDisabled(true);

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            // eslint-disable-next-line no-useless-return
            return;
        }

        createHelpPlacingOrderForm({
            Name: fullname,
            CompanyName: companyName,
            Email: emailAddress,
            Message: message,
            onSuccess: () => {
                setIsModalOpen(true);
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                // eslint-disable-next-line react/no-this-in-sfc
                this.onSuccess?.();
            },
        });
    };

    const emptyFields = () => {
        setCompanyName("");
        setEmailAddress("");
        setFullname("");
        setMessage("");
    };

    const modalCloseHandler = () => setIsModalOpen(false);

    const continueButtonClickHandler = () => {
        setIsModalOpen(false);
        emptyFields();
        window.location.href = "/";
    };

    return (
        <StyledWrapper {...styles.mainContainer}>
            <StyledWrapper {...styles.container}>
                <TextField
                    {...styles.inputStyles}
                    label={translate("Name*")}
                    placeholder={translate("First and Last Name")}
                    value={fullname}
                    error={isSubmitted && fullnameError}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => fullnameChangeHandler(e)}
                />
                <TextField
                    {...styles.inputStyles}
                    label={translate("Company Name")}
                    placeholder={translate("Company")}
                    value={companyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => companyNameChangeHandler(e)}
                />
                <TextField
                    {...styles.inputStyles}
                    label={translate("Email*")}
                    placeholder={translate("Email")}
                    value={emailAddress}
                    error={isSubmitted && emailAddressError}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => emailChangeHandler(e)}
                />
                <TextArea
                    label={translate("Message*")}
                    placeholder={translate("How can we help?")}
                    {...styles.textAreaStyles}
                    value={message}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => messageChangeHandler(e)}
                    error={isSubmitted && messageError}
                ></TextArea>

                <StyledWrapper {...styles.captchaStyles}>
                    <ReCaptcha location="ContactUs" recaptchaRef={recaptchaRef} />
                </StyledWrapper>

                <Button
                    {...styles.submitButton}
                    type="submit"
                    onClick={submitFormHandler}
                    disabled={isSubmitted && !isSubmitEnabled}
                >
                    {fields.submitButtonText}
                </Button>

                <Modal
                    headline={translate("Message Sent")}
                    contentLabel={fields.successMessage}
                    {...styles.successModal}
                    isOpen={isModalOpen}
                    handleClose={modalCloseHandler}
                >
                    <GridContainer {...styles.modalContainer}>
                        <GridItem {...styles.messageTextGridItem}>
                            <Typography {...styles.messageText}>{fields.successMessage}</Typography>
                        </GridItem>
                        <GridItem {...styles.buttonGridItem}>
                            <Button {...styles.continueButton} onClick={continueButtonClickHandler}>
                                {translate("Continue")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </Modal>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENHelpPlacingOrderForm),
    definition: {
        group: "Contact Us",
        icon: "inputStyles-pipe",
        displayName: "Help Placing An Order Form",
        fieldDefinitions: [
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
